// MaterialScanningSection.jsx
// Reusable component for scanning inventory barcodes during service completion
// Used in both Technician App and Admin/Specialist interfaces

import React, { Component } from 'react';
import { Button, Form, Alert, Modal } from 'react-bootstrap';
import BarcodeScanner from '../common/BarcodeScanner';
import InventoryAPI from '../../services/inventoryService';
import { tracksInventory as installerTracksInventory } from '../../util/materialsTracking';
import { formatProductLabel } from '../../util/productLabel';

/**
 * Does this shop track stock levels, or only what was used?
 *
 * Prop first, cached installer as a fallback. Both are needed: the job view can
 * pass it, but the two technician surfaces — where materials are actually
 * captured when a service is finished — have no installer row to pass.
 *
 * The cache read is unambiguous here in a way it is not everywhere: material
 * capture only ever happens installer-side, so `companyAPI` is the installer's
 * own row rather than a dealership's.
 */
function shopTracksInventory(props) {
  if (typeof props.tracksInventory === 'boolean') return props.tracksInventory;
  try {
    return installerTracksInventory(JSON.parse(localStorage.getItem('companyAPI')));
  } catch {
    // Unreadable cache falls back to the pre-setting behaviour rather than
    // silently dropping a shop's stock tracking.
    return true;
  }
}


class MaterialScanningSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            scannedMaterials: [],
            manualBarcode: '',
            lookupError: null,
            lookupLoading: false,

            // Scanner modal
            showScanner: false,
            scannerError: null,

            // Manual entry (for non-inventory materials)
            showManualEntry: false,
            manualProduct: {
                product_id: '',
                brand: '',
                product_name: '',
                quantity_used: ''
            },

            // Product selection
            products: [],
            categories: [],
            units: [],
            brands: [],
            productsLoading: false,
            selectedBrand: '',
            showAddNewProduct: false,
            showAddNewBrandManual: false,
            newBrandNameManual: '',
            newProduct: {
                brand: '',
                name: ''
            },

            // Barcode not found flow
            showBarcodeNotFound: false,
            notFoundBarcode: '',

            // Multiple products found for same barcode
            showProductSelection: false,
            multipleProducts: [],
            pendingBarcode: '',

            notFoundForm: {
                product_id: '',
                category_id: '',
                brand: '',
                product_name: '',
                unit_id: '',
                quantity_on_roll: '',
                quantity_used: ''
            },

            // Category mismatch flow
            showCategoryMismatch: false,
            mismatchBarcode: '',
            mismatchProductInfo: null, // { category_name, brand, product_name }
            showAddNewBrand: false,
            newBrandName: '',
            isNewProduct: false,
            notFoundSelectedBrand: '',  // Track selected brand for filtering products
            isAddingNewProductName: false,  // Track if user is typing a new product name

            // Manual entry → inventory linking
            manualInventoryItems: [],        // Available inventory items for selected product
            manualInventoryLoading: false,   // Loading state for inventory lookup
            selectedInventoryItemId: null,    // Which inventory item the tech picked
            selectedItemQtyRemaining: null,   // For stock warning
            showManualReceive: false,         // Inline receive form visible
            manualReceiveQty: '',             // Quantity for inline receive
            manualReceiveBarcode: '',         // Optional barcode for inline receive
            manualReceiveLoading: false       // Loading state for inline receive
        };

    }

    componentWillUnmount() {
        // No scanner cleanup needed — BarcodeScanner handles its own lifecycle
    }

    // Check if device is mobile
    isMobile = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    // =========================================================================
    // Barcode Not Found Flow
    // =========================================================================

    showBarcodeNotFoundForm = async (barcode) => {
        const { defaultUnitId, expectedQty } = this.props;

        this.setState({
            showBarcodeNotFound: true,
            notFoundBarcode: barcode,
            lookupLoading: false,
            manualBarcode: '',
            notFoundForm: {
                product_id: '',
                brand: '',
                product_name: '',
                unit_id: defaultUnitId ? defaultUnitId.toString() : '',  // Auto-populate from category config
                quantity_on_roll: '',
                quantity_used: expectedQty || ''
            },
            showAddNewBrand: false,
            newBrandName: '',
            isNewProduct: true,
            productsLoading: true
        });

        // Load products and units for dropdowns - filter by category
        try {
            const { installerId, categoryId } = this.props;
            const [products, units] = await Promise.all([
                InventoryAPI.getProducts(installerId, categoryId),  // Pass categoryId to filter products
                InventoryAPI.getUnitsOfMeasure()
            ]);
            this.setState({
                products: products || [],
                units: units || [],
                productsLoading: false
            });
        } catch (error) {
            console.error('Error loading data for not found form:', error);
            this.setState({ productsLoading: false });
        }
    };

    handleCloseBarcodeNotFound = () => {
        this.setState({
            showBarcodeNotFound: false,
            notFoundBarcode: '',
            showAddNewBrand: false,
            newBrandName: '',
            notFoundSelectedBrand: '',
            isAddingNewProductName: false
        });
    };

    // =========================================================================
    // Category Mismatch Flow
    // =========================================================================

    handleCloseCategoryMismatch = () => {
        this.setState({
            showCategoryMismatch: false,
            mismatchBarcode: '',
            mismatchProductInfo: null
        });
    };

    handleAddForCurrentCategory = () => {
        const { mismatchBarcode } = this.state;
        // Close mismatch dialog and open the "barcode not found" form
        // This allows user to add the barcode for the current category
        this.setState({
            showCategoryMismatch: false,
            mismatchProductInfo: null
        }, () => {
            // Trigger the barcode not found flow with the scanned barcode
            this.showBarcodeNotFoundForm(mismatchBarcode);
        });
    };

    handleNotFoundFormChange = (field, value) => {
        this.setState(prev => ({
            notFoundForm: {
                ...prev.notFoundForm,
                [field]: value
            }
        }));
    };

    handleNotFoundBrandChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddNewBrand: true,
                notFoundSelectedBrand: '',
                isAddingNewProductName: true,
                notFoundForm: { ...this.state.notFoundForm, brand: '', product_id: '', product_name: '' }
            });
        } else {
            this.setState({
                showAddNewBrand: false,
                newBrandName: '',
                notFoundSelectedBrand: value,
                isAddingNewProductName: false,  // Reset - user can select existing product
                notFoundForm: { ...this.state.notFoundForm, brand: value, product_id: '', product_name: '' }
            });
        }
    };

    handleNotFoundNewBrandChange = (e) => {
        const value = e.target.value;
        this.setState({
            newBrandName: value,
            notFoundForm: { ...this.state.notFoundForm, brand: value }
        });
    };

    handleCancelNotFoundNewBrand = () => {
        this.setState({
            showAddNewBrand: false,
            newBrandName: '',
            notFoundForm: { ...this.state.notFoundForm, brand: '' }
        });
    };

    handleNotFoundProductChange = (e) => {
        const productId = e.target.value;

        if (productId === 'add_new') {
            // User wants to create a new product
            this.setState({
                isNewProduct: true,
                notFoundForm: {
                    ...this.state.notFoundForm,
                    product_id: '',
                    brand: '',
                    product_name: ''
                }
            });
            return;
        }

        // User selected an existing product
        const { products } = this.state;
        const selectedProduct = products.find(p =>
            (p.product_id || p.id)?.toString() === productId
        );

        if (selectedProduct) {
            this.setState({
                isNewProduct: false,
                notFoundForm: {
                    ...this.state.notFoundForm,
                    product_id: productId,
                    brand: selectedProduct.brand || '',
                    product_name: selectedProduct.product_name || selectedProduct.name || '',
                    unit_id: selectedProduct.unit_id?.toString() || ''
                }
            });
        }
    };

    // Get unique brands from products
    getUniqueBrands = () => {
        const { products } = this.state;
        return products
            .map(p => p.brand)
            .filter(brand => brand && brand.trim())
            .filter((brand, index, self) => self.indexOf(brand) === index)
            .sort();
    };

    // Get products filtered by selected brand in the not-found flow
    getProductsForSelectedBrand = () => {
        const { products, notFoundSelectedBrand } = this.state;
        if (!notFoundSelectedBrand) return [];

        return products
            .filter(p => p.brand === notFoundSelectedBrand)
            .sort((a, b) => {
                const nameA = (a.product_name || a.name || '').toLowerCase();
                const nameB = (b.product_name || b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
    };

    // Get unit name from units array based on unit_id
    getUnitName = () => {
        const { units } = this.state;
        const { defaultUnitId } = this.props;
        if (!defaultUnitId || !units || units.length === 0) return '';
        const unit = units.find(u => u.unit_id === defaultUnitId || u.unit_id?.toString() === defaultUnitId?.toString());
        return unit ? unit.unit_name : '';
    };

    // Get unit name for a specific unit_id
    getUnitNameById = (unitId) => {
        const { units } = this.state;
        if (!unitId || !units || units.length === 0) return '';
        const unit = units.find(u => u.unit_id === unitId || u.unit_id?.toString() === unitId?.toString());
        return unit ? unit.unit_name : '';
    };

    // Handle product selection from brand-filtered dropdown in not-found flow
    handleNotFoundBrandProductSelect = (e) => {
        const productId = e.target.value;

        if (productId === 'add_new') {
            // User wants to create a new product for this brand
            this.setState({
                isAddingNewProductName: true,
                notFoundForm: {
                    ...this.state.notFoundForm,
                    product_id: '',
                    product_name: ''
                }
            });
            return;
        }

        // User selected an existing product for this brand
        const { products, notFoundSelectedBrand } = this.state;
        const { defaultUnitId } = this.props;
        const selectedProduct = products.find(p =>
            (p.product_id || p.id)?.toString() === productId
        );

        if (selectedProduct) {
            this.setState({
                isAddingNewProductName: false,
                isNewProduct: false,  // Using existing product
                notFoundForm: {
                    ...this.state.notFoundForm,
                    product_id: productId,
                    brand: notFoundSelectedBrand,
                    product_name: selectedProduct.product_name || selectedProduct.name || '',
                    unit_id: selectedProduct.unit_id?.toString() || defaultUnitId?.toString() || ''
                }
            });
        }
    };

    handleNotFoundSubmit = async () => {
        const { notFoundBarcode, notFoundForm, isNewProduct, scannedMaterials, isAddingNewProductName, notFoundSelectedBrand, showAddNewBrand, products } = this.state;
        const { installerId, categoryId, categoryName } = this.props;

        // Determine if we're actually creating a new product:
        // - Either user is in "add new product" mode (no product_id selected)
        // - Or user is explicitly typing a new product name
        const needsNewProduct = !notFoundForm.product_id && (isNewProduct || isAddingNewProductName || showAddNewBrand);

        // Validation
        if (needsNewProduct && !notFoundForm.product_name.trim()) {
            alert('Please enter a product name');
            return;
        }
        if (!needsNewProduct && !notFoundForm.product_id) {
            alert('Please select a product');
            return;
        }

        // Validate category matches if an existing product was selected
        if (!needsNewProduct && notFoundForm.product_id && categoryId) {
            const selectedProduct = products.find(p =>
                (p.product_id || p.id)?.toString() === notFoundForm.product_id?.toString()
            );
            if (selectedProduct && selectedProduct.category_id) {
                const expectedCategoryId = parseInt(categoryId, 10);
                const productCategoryId = parseInt(selectedProduct.category_id, 10);
                if (productCategoryId !== expectedCategoryId) {
                    const productCategoryName = selectedProduct.category_name || 'different category';
                    const expectedCategoryName = categoryName || 'this service category';
                    alert(`This product is for ${productCategoryName}, not ${expectedCategoryName}. Please select the correct product.`);
                    return;
                }
            }
        }
        if (!notFoundForm.quantity_on_roll || parseFloat(notFoundForm.quantity_on_roll) <= 0) {
            alert('Please enter the quantity on the roll');
            return;
        }
        if (!notFoundForm.quantity_used || parseFloat(notFoundForm.quantity_used) <= 0) {
            alert('Please enter the quantity used');
            return;
        }

        this.setState({ productsLoading: true });

        try {
            let productId = notFoundForm.product_id;

            // If new product, create it first
            if (needsNewProduct) {
                const productData = {
                    name: notFoundForm.product_name.trim(),
                    brand: notFoundForm.brand.trim() || notFoundSelectedBrand || '',
                    category_id: categoryId || null,
                    unit_id: parseInt(notFoundForm.unit_id, 10) || null,
                    installer_id: installerId
                };

                const createdProduct = await InventoryAPI.createProduct(productData);
                productId = createdProduct.product_id || createdProduct.id;
            }

            // Create inventory item with the barcode
            const inventoryData = {
                productId: parseInt(productId, 10),
                installerId: installerId,
                barcode: notFoundBarcode,
                quantity: parseFloat(notFoundForm.quantity_on_roll),
                locationId: this.props.locationId || null
            };

            const newItem = await InventoryAPI.receiveInventory(inventoryData);
            const itemId = newItem.item_id || newItem.id;
            const quantityOnRoll = parseFloat(notFoundForm.quantity_on_roll);
            const quantityUsed = parseFloat(notFoundForm.quantity_used);

            // Calculate remaining quantity after use
            // Note: Inventory deduction happens at modal submit time in ItemMaterialCaptureModal
            const quantityRemaining = quantityOnRoll - quantityUsed;

            // Add to scanned materials
            const material = {
                inventory_item_id: itemId,
                barcode: notFoundBarcode,
                brand: notFoundForm.brand || notFoundSelectedBrand || '',
                product_name: notFoundForm.product_name || '',
                quantity_remaining: quantityRemaining,
                unit_code: '',
                quantity_used: notFoundForm.quantity_used
            };

            const newMaterials = [...scannedMaterials, material];
            this.setState({
                scannedMaterials: newMaterials,
                showBarcodeNotFound: false,
                notFoundBarcode: '',
                productsLoading: false
            });
            this.notifyChange(newMaterials);

        } catch (error) {
            console.error('Error creating product/inventory:', error);
            if (error.response && error.response.status === 409) {
                alert(error.response.data.message);
            } else {
                alert('Error saving. Please try again.');
            }
            this.setState({ productsLoading: false });
        }
    };

    // =========================================================================
    // Barcode Lookup
    // =========================================================================

    handleManualBarcodeChange = (e) => {
        this.setState({
            manualBarcode: e.target.value,
            lookupError: null
        });
    };

    handleManualBarcodeSubmit = async (e) => {
        e.preventDefault();
        const { manualBarcode } = this.state;
        if (manualBarcode.trim()) {
            await this.lookupBarcode(manualBarcode.trim());
        }
    };

    lookupBarcode = async (barcode) => {
        const { scannedMaterials } = this.state;
        const { installerId } = this.props;

        console.log('[MaterialScanningSection] Looking up barcode:', barcode, 'installerId:', installerId);

        // Check if already scanned
        if (scannedMaterials.some(m => m.barcode === barcode)) {
            this.setState({ lookupError: 'This barcode has already been scanned' });
            return;
        }

        this.setState({ lookupLoading: true, lookupError: null });

        try {
            const result = await InventoryAPI.lookupBarcode(barcode, installerId);
            console.log('[MaterialScanningSection] Lookup result:', result);

            // Check if barcode was found
            // API returns { found, multiple, items: [...], data: {...} }
            if (result && result.found && result.items && result.items.length > 0) {
                if (result.multiple && result.items.length > 1) {
                    // Multiple products found - show selection modal
                    this.setState({
                        showProductSelection: true,
                        multipleProducts: result.items,
                        pendingBarcode: barcode,
                        lookupLoading: false,
                        manualBarcode: ''
                    });
                    return;
                }

                // Single item found
                const item = result.items[0];
                this.processFoundItem(item, barcode);
            } else {
                // Barcode not found - show the "add to inventory" form
                console.log('[MaterialScanningSection] Barcode not found, showing add form');
                this.showBarcodeNotFoundForm(barcode);
            }
        } catch (error) {
            console.error('[MaterialScanningSection] Error looking up barcode:', error);
            if (error.response?.status === 404) {
                // Barcode not found - show the "add to inventory" form
                this.showBarcodeNotFoundForm(barcode);
            } else {
                this.setState({
                    lookupError: 'Error looking up barcode. Please try again.',
                    lookupLoading: false
                });
            }
        }
    };

    // Process a found inventory item (after lookup or selection)
    processFoundItem = (item, barcode) => {
        const { scannedMaterials } = this.state;
        const { expectedQty, categoryId, categoryName } = this.props;

        // Validate category matches if categoryId is provided
        const expectedCategoryId = categoryId ? parseInt(categoryId, 10) : null;
        const resultCategoryId = item.category_id ? parseInt(item.category_id, 10) : null;

        console.log('[MaterialScanningSection] Category validation:', {
            expectedCategoryId,
            resultCategoryId,
            categoryName,
            resultCategoryName: item.category_name,
            willValidate: !!(expectedCategoryId && resultCategoryId)
        });

        if (expectedCategoryId && resultCategoryId && resultCategoryId !== expectedCategoryId) {
            // Show category mismatch dialog with option to add for current category
            this.setState({
                showCategoryMismatch: true,
                mismatchBarcode: barcode,
                mismatchProductInfo: {
                    category_name: item.category_name || 'different category',
                    expected_category_name: categoryName || 'this service category',
                    brand: item.brand || '',
                    product_name: item.product_name || '',
                    variant_label: item.variant_label || ''
                },
                lookupLoading: false,
                manualBarcode: ''
            });
            return;
        }

        const material = {
            inventory_item_id: item.id,
            barcode: item.barcode || barcode,
            brand: item.brand || '',
            product_name: item.product_name || '',
            lot_number: item.lot_number || '',
            quantity_remaining: parseFloat(item.quantity_remaining) || 0,
            unit_code: item.unit_code || '',
            quantity_used: expectedQty || ''
        };

        const newMaterials = [...scannedMaterials, material];
        this.setState({
            scannedMaterials: newMaterials,
            manualBarcode: '',
            lookupLoading: false,
            showProductSelection: false,
            multipleProducts: [],
            pendingBarcode: ''
        });
        this.notifyChange(newMaterials);
    };

    // Handle selection from multiple products modal
    handleSelectProduct = (item) => {
        const { pendingBarcode } = this.state;
        this.processFoundItem(item, pendingBarcode);
    };

    // Cancel product selection
    handleCancelProductSelection = () => {
        this.setState({
            showProductSelection: false,
            multipleProducts: [],
            pendingBarcode: ''
        });
    };

    // =========================================================================
    // Material Management
    // =========================================================================

    handleQuantityChange = (index, value) => {
        const { scannedMaterials } = this.state;
        const newMaterials = [...scannedMaterials];
        newMaterials[index] = {
            ...newMaterials[index],
            quantity_used: value
        };
        this.setState({ scannedMaterials: newMaterials });
        this.notifyChange(newMaterials);
    };

    handleRemoveMaterial = (index) => {
        const { scannedMaterials } = this.state;
        const newMaterials = scannedMaterials.filter((_, i) => i !== index);
        this.setState({ scannedMaterials: newMaterials });
        this.notifyChange(newMaterials);
    };

    // =========================================================================
    // Manual Entry (non-inventory materials)
    // =========================================================================

    handleShowManualEntry = async () => {
        this.setState({
            showManualEntry: true,
            showAddNewProduct: false,
            manualProduct: {
                product_id: '',
                brand: '',
                product_name: '',
                quantity_used: this.props.expectedQty || '',
                unit_id: ''
            },
            selectedBrand: '',
            productsLoading: true,
            manualInventoryItems: [],
            manualInventoryLoading: false,
            selectedInventoryItemId: null,
            selectedItemQtyRemaining: null,
            showManualReceive: false,
            manualReceiveQty: '',
            manualReceiveBarcode: '',
            manualReceiveLoading: false
        });

        // Load products and units for dropdown (filtered by category)
        try {
            const { installerId, categoryId } = this.props;
            console.log('[MaterialScanningSection] Loading products with installerId:', installerId, 'categoryId:', categoryId);
            const [products, units] = await Promise.all([
                InventoryAPI.getProducts(installerId, categoryId),
                InventoryAPI.getUnitsOfMeasure()
            ]);
            this.setState({ products: products || [], units: units || [], productsLoading: false });
        } catch (error) {
            console.error('Error loading products:', error);
            this.setState({ products: [], productsLoading: false });
        }
    };

    handleHideManualEntry = () => {
        this.setState({
            showManualEntry: false,
            showAddNewProduct: false,
            showAddNewBrandManual: false,
            newBrandNameManual: ''
        });
    };

    handleBrandSelect = (e) => {
        const brand = e.target.value;

        if (brand === 'add_new_brand') {
            // When adding new brand, go directly to creating a new product with new brand
            this.setState({
                showAddNewBrandManual: true,
                selectedBrand: '',
                showAddNewProduct: false,
                newBrandNameManual: '',
                newProduct: { brand: '', name: '' },
                manualProduct: {
                    ...this.state.manualProduct,
                    product_id: '',
                    brand: '',
                    product_name: ''
                }
            });
            return;
        }

        this.setState({
            selectedBrand: brand,
            showAddNewProduct: false,
            showAddNewBrandManual: false,
            newBrandNameManual: '',
            newProduct: { brand: '', name: '' },
            manualProduct: {
                ...this.state.manualProduct,
                product_id: '',
                brand: brand,
                product_name: ''
            }
        });
    };

    handleNewBrandManualChange = (e) => {
        this.setState({ newBrandNameManual: e.target.value });
    };

    handleNewProductNameChange = (e) => {
        this.setState(prev => ({
            newProduct: { ...prev.newProduct, name: e.target.value }
        }));
    };

    handleCreateNewBrandAndProduct = async () => {
        const { newBrandNameManual, newProduct } = this.state;
        const { installerId, categoryId, defaultUnitId } = this.props;

        const brandName = newBrandNameManual.trim();
        const productName = newProduct.name?.trim() || '';

        if (!brandName) {
            alert('Please enter a brand name');
            return;
        }

        if (!productName) {
            alert('Please enter a product name');
            return;
        }

        try {
            this.setState({ productsLoading: true });

            // Create the product with the new brand
            const productData = {
                name: productName,
                brand: brandName,
                categoryId: categoryId || null,
                installerId: installerId,
                unitId: defaultUnitId || null
            };

            console.log('[MaterialScanningSection] Creating product with data:', productData);
            const createdProduct = await InventoryAPI.createProduct(productData);
            const newProductId = createdProduct.product_id || createdProduct.id;

            // Add to products list and select it
            this.setState(prev => ({
                products: [...prev.products, createdProduct],
                selectedBrand: brandName,
                showAddNewBrandManual: false,
                showAddNewProduct: false,
                productsLoading: false,
                newBrandNameManual: '',
                newProduct: { brand: '', name: '' },
                manualProduct: {
                    ...prev.manualProduct,
                    product_id: newProductId?.toString() || '',
                    brand: brandName,
                    product_name: productName
                }
            }));
        } catch (error) {
            console.error('Error creating product:', error);
            if (error.response && error.response.status === 409) {
                alert(error.response.data.message);
            } else {
                alert('Error creating product. Please try again.');
            }
            this.setState({ productsLoading: false });
        }
    };

    handleCancelNewBrandManual = () => {
        this.setState({
            showAddNewBrandManual: false,
            newBrandNameManual: '',
            newProduct: { brand: '', name: '' }
        });
    };

    handleProductSelect = async (e) => {
        const productId = e.target.value;

        if (productId === 'add_new') {
            this.setState({
                showAddNewProduct: true,
                manualProduct: {
                    ...this.state.manualProduct,
                    product_id: '',
                    product_name: '',
                    unit_id: ''
                },
                manualInventoryItems: [],
                selectedInventoryItemId: null,
                selectedItemQtyRemaining: null,
                showManualReceive: false
            });
            return;
        }

        const { products } = this.state;
        const selectedProduct = products.find(p =>
            (p.product_id || p.id)?.toString() === productId
        );

        if (selectedProduct) {
            this.setState(prev => ({
                showAddNewProduct: false,
                manualProduct: {
                    ...prev.manualProduct,
                    product_id: productId,
                    brand: selectedProduct.brand || prev.selectedBrand || '',
                    product_name: selectedProduct.product_name || selectedProduct.name || '',
                    unit_id: selectedProduct.unit_id || ''
                },
                manualInventoryItems: [],
                selectedInventoryItemId: null,
                selectedItemQtyRemaining: null,
                showManualReceive: false,
                manualInventoryLoading: true
            }));

            // Check for available inventory items
            try {
                const { installerId } = this.props;
                const items = await InventoryAPI.getInventoryItems(productId, installerId, 'in_stock', this.props.locationId);
                const activeItems = (items || []).filter(item => parseFloat(item.quantity_remaining) > 0);
                this.setState({
                    manualInventoryItems: activeItems,
                    manualInventoryLoading: false,
                    showManualReceive: activeItems.length === 0
                });
            } catch (error) {
                console.error('[MaterialScanningSection] Error checking inventory:', error);
                this.setState({
                    manualInventoryItems: [],
                    manualInventoryLoading: false,
                    showManualReceive: true
                });
            }
        }
    };

    // Get products filtered by selected brand, sorted alphabetically
    getFilteredProducts = () => {
        const { products, selectedBrand } = this.state;
        let filtered = selectedBrand
            ? products.filter(p => p.brand === selectedBrand)
            : products;

        // Sort alphabetically by product name
        return filtered.sort((a, b) => {
            const nameA = (a.product_name || a.name || '').toLowerCase();
            const nameB = (b.product_name || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    };

    handleNewProductChange = (field, value) => {
        this.setState(prev => ({
            newProduct: {
                ...prev.newProduct,
                [field]: value
            }
        }));
    };

    handleCreateNewProduct = async () => {
        const { newProduct, selectedBrand } = this.state;
        const { installerId, categoryId, defaultUnitId } = this.props;

        // Use selectedBrand as the brand (already selected before adding product)
        const brandToUse = selectedBrand || '';

        if (!brandToUse.trim()) {
            alert('Please select a brand before adding a new product');
            return;
        }

        if (!newProduct.name.trim()) {
            alert('Please enter a product name');
            return;
        }

        try {
            this.setState({ productsLoading: true });

            // Create the product with categoryId and default unit
            const productData = {
                name: newProduct.name.trim(),
                brand: brandToUse.trim(),
                categoryId: categoryId || null,
                installerId: installerId,
                unitId: defaultUnitId || null
            };

            const createdProduct = await InventoryAPI.createProduct(productData);

            // Add to products list and select it
            const newProductId = createdProduct.product_id || createdProduct.id;

            this.setState(prev => ({
                products: [...prev.products, createdProduct],
                showAddNewProduct: false,
                productsLoading: false,
                newProduct: { brand: '', name: '' },
                manualProduct: {
                    ...prev.manualProduct,
                    product_id: newProductId?.toString() || '',
                    brand: brandToUse.trim(),
                    product_name: newProduct.name.trim()
                }
            }));
        } catch (error) {
            console.error('Error creating product:', error);
            if (error.response && error.response.status === 409) {
                alert(error.response.data.message);
            } else {
                alert('Error creating product. Please try again.');
            }
            this.setState({ productsLoading: false });
        }
    };

    handleManualQuantityChange = (value) => {
        this.setState(prev => ({
            manualProduct: {
                ...prev.manualProduct,
                quantity_used: value
            }
        }));
    };

    handleInventoryItemSelect = (itemId) => {
        const { manualInventoryItems } = this.state;
        const item = manualInventoryItems.find(i => i.id?.toString() === itemId?.toString());
        this.setState({
            selectedInventoryItemId: itemId ? parseInt(itemId, 10) : null,
            selectedItemQtyRemaining: item ? parseFloat(item.quantity_remaining) : null,
            showManualReceive: false
        });
    };

    handleShowManualReceive = () => {
        this.setState({
            showManualReceive: true,
            selectedInventoryItemId: null,
            selectedItemQtyRemaining: null,
            manualReceiveQty: '',
            manualReceiveBarcode: ''
        });
    };

    handleManualReceiveSubmit = async () => {
        const { manualProduct, manualReceiveQty, manualReceiveBarcode } = this.state;
        const { installerId } = this.props;

        if (!manualReceiveQty || parseFloat(manualReceiveQty) <= 0) {
            alert('Please enter a valid quantity to receive');
            return;
        }

        this.setState({ manualReceiveLoading: true });

        try {
            const result = await InventoryAPI.receiveInventory({
                productId: parseInt(manualProduct.product_id, 10),
                installerId: parseInt(installerId, 10),
                barcode: manualReceiveBarcode.trim() || null,
                quantity: parseFloat(manualReceiveQty),
                locationId: this.props.locationId || null
            });

            const newItemId = result.item?.id || result.id || result.data?.id;
            const newQtyRemaining = parseFloat(manualReceiveQty);

            this.setState({
                manualReceiveLoading: false,
                selectedInventoryItemId: newItemId,
                selectedItemQtyRemaining: newQtyRemaining,
                showManualReceive: false,
                manualInventoryItems: [{
                    id: newItemId,
                    barcode: manualReceiveBarcode.trim() || null,
                    quantity_remaining: newQtyRemaining
                }]
            });
        } catch (error) {
            console.error('[MaterialScanningSection] Error receiving inventory:', error);
            alert('Error receiving inventory. Please try again.');
            this.setState({ manualReceiveLoading: false });
        }
    };

    handleManualProductSubmit = () => {
        const { manualProduct, scannedMaterials, products } = this.state;
        const { categoryId, categoryName } = this.props;

        if (!manualProduct.product_name.trim()) {
            alert('Please select or add a product');
            return;
        }

        if (!manualProduct.quantity_used || parseFloat(manualProduct.quantity_used) <= 0) {
            alert('Please enter a valid quantity');
            return;
        }

        // Validate category matches if product was selected from dropdown
        if (manualProduct.product_id && categoryId) {
            const selectedProduct = products.find(p =>
                (p.product_id || p.id)?.toString() === manualProduct.product_id?.toString()
            );
            if (selectedProduct && selectedProduct.category_id) {
                const expectedCategoryId = parseInt(categoryId, 10);
                const productCategoryId = parseInt(selectedProduct.category_id, 10);
                if (productCategoryId !== expectedCategoryId) {
                    const productCategoryName = selectedProduct.category_name || 'different category';
                    const expectedCategoryName = categoryName || 'this service category';
                    alert(`This product is for ${productCategoryName}, not ${expectedCategoryName}. Please select the correct product.`);
                    return;
                }
            }
        }

        const { selectedInventoryItemId, selectedItemQtyRemaining, manualInventoryItems } = this.state;

        // Find the selected inventory item for barcode/lot info
        const selectedItem = selectedInventoryItemId
            ? manualInventoryItems.find(i => i.id?.toString() === selectedInventoryItemId?.toString())
            : null;

        const hasInventoryLink = !!selectedInventoryItemId;

        const material = {
            inventory_item_id: selectedInventoryItemId || null,
            product_id: manualProduct.product_id || null,
            barcode: selectedItem?.barcode || null,
            brand: manualProduct.brand.trim(),
            product_name: manualProduct.product_name.trim(),
            quantity_remaining: hasInventoryLink ? selectedItemQtyRemaining : null,
            unit_code: selectedItem?.unit_code || '',
            quantity_used: manualProduct.quantity_used,
            lot_number: selectedItem?.lot_number || null,
            isManualEntry: !hasInventoryLink // Only flag as manual if no inventory link
        };

        const newMaterials = [...scannedMaterials, material];
        this.setState({
            scannedMaterials: newMaterials,
            showManualEntry: false,
            showAddNewProduct: false,
            manualProduct: { product_id: '', brand: '', product_name: '', quantity_used: '' },
            manualInventoryItems: [],
            selectedInventoryItemId: null,
            selectedItemQtyRemaining: null,
            showManualReceive: false,
            manualReceiveQty: '',
            manualReceiveBarcode: ''
        });
        this.notifyChange(newMaterials);
    };

    notifyChange = (materials) => {
        const { onMaterialsChange, onMaterialScanned } = this.props;
        if (onMaterialsChange) {
            onMaterialsChange(materials);
        }
        // Also notify parent of the scanned material details for auto-population
        if (onMaterialScanned && materials.length > 0) {
            const latestMaterial = materials[materials.length - 1];
            onMaterialScanned({
                product_brand: latestMaterial.brand,
                product_name: latestMaterial.product_name,
                roll_number: latestMaterial.barcode,
                lot_number: latestMaterial.lot_number
            });
        }
    };

    // =========================================================================
    // Barcode Scanner (Camera)
    // =========================================================================

    handleOpenScanner = () => {
        this.setState({ showScanner: true, scannerError: null });
    };

    handleCloseScanner = () => {
        this.setState({ showScanner: false, scannerError: null });
    };

    handleScanSuccess = async (rawValue) => {
        this.setState({ showScanner: false });
        await this.lookupBarcode(rawValue);
    };

    // =========================================================================
    // Render
    // =========================================================================

    renderScannedMaterial = (material, index) => {
        const { quantity_used, quantity_remaining, unit_code, isManualEntry } = material;
        const qtyUsed = parseFloat(quantity_used) || 0;
        const showWarning = !isManualEntry && quantity_remaining && qtyUsed > quantity_remaining;

        return (
            <div key={index} className={`scanned-material-card ${isManualEntry ? 'manual-entry-card' : ''}`}>
                <div className="material-header">
                    <div className="material-name">
                        {material.brand && <span className="brand">{material.brand} </span>}
                        {material.product_name}
                        {material.variant_label && ` (${material.variant_label})`}
                        {isManualEntry && <span className="manual-badge">Manual</span>}
                    </div>
                    <button
                        className="remove-btn"
                        onClick={() => this.handleRemoveMaterial(index)}
                        title="Remove"
                    >
                        <i className="fa-sharp fa-regular fa-times"></i>
                    </button>
                </div>
                {!isManualEntry && (
                    <div className="material-details">
                        <div className="detail-row">
                            <span className="label">Roll:</span>
                            <code>{material.barcode}</code>
                        </div>
                        {material.lot_number && (
                            <div className="detail-row">
                                <span className="label">Lot:</span>
                                <code>{material.lot_number}</code>
                            </div>
                        )}
                        <div className="detail-row">
                            <span className="label">Available:</span>
                            <span>{quantity_remaining?.toFixed(2) || '0'} {unit_code}</span>
                        </div>
                    </div>
                )}
                <div className="quantity-input-row">
                    <label>Quantity Used:</label>
                    <div className="quantity-input-group">
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={quantity_used}
                            onChange={(e) => this.handleQuantityChange(index, e.target.value)}
                            className={showWarning ? 'is-warning' : ''}
                        />
                        <span className="unit-label">{unit_code}</span>
                    </div>
                </div>
                {showWarning && (
                    <div className="quantity-warning">
                        <i className="fa-sharp fa-regular fa-exclamation-triangle"></i>
                        Quantity exceeds available stock
                    </div>
                )}
            </div>
        );
    };

    renderManualEntryForm() {
        const {
            showManualEntry, manualProduct, products, productsLoading, showAddNewProduct,
            newProduct, selectedBrand, showAddNewBrandManual, newBrandNameManual,
            manualInventoryItems, manualInventoryLoading, selectedInventoryItemId,
            selectedItemQtyRemaining, showManualReceive, manualReceiveQty,
            manualReceiveBarcode, manualReceiveLoading
        } = this.state;

        if (!showManualEntry) return null;

        // Stock warning for manual entry with linked inventory
        const manualQtyUsed = parseFloat(manualProduct.quantity_used) || 0;
        const showManualStockWarning = selectedInventoryItemId && selectedItemQtyRemaining != null && manualQtyUsed > selectedItemQtyRemaining;

        // Get unique brands from products
        const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();

        // Get products filtered by selected brand
        const filteredProducts = this.getFilteredProducts();

        return (
            <div className="manual-entry-form">
                <div className="manual-entry-header">
                    <strong>Add Material (No Barcode)</strong>
                    <button className="close-btn" onClick={this.handleHideManualEntry}>
                        <i className="fa-sharp fa-regular fa-times"></i>
                    </button>
                </div>

                {productsLoading ? (
                    <div className="text-muted small py-3 text-center">
                        <i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Loading products...
                    </div>
                ) : (
                    <>
                        {/* Add New Brand + Product Form (combined) */}
                        {showAddNewBrandManual ? (
                            <div className="add-new-product-form">
                                <div className="mb-2">
                                    <strong><i className="fa-sharp fa-regular fa-plus-circle mr-1"></i> Add New Brand & Product</strong>
                                </div>
                                <Form.Group className="mb-2">
                                    <Form.Label className="small">Brand Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        value={newBrandNameManual}
                                        onChange={this.handleNewBrandManualChange}
                                        placeholder="e.g., XPEL, 3M, SunTek"
                                        autoFocus
                                    />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="small">Product Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        value={newProduct?.name || ''}
                                        onChange={this.handleNewProductNameChange}
                                        placeholder="e.g., Ultimate Plus PPF"
                                    />
                                </Form.Group>
                                <div className="d-flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={this.handleCancelNewBrandManual}
                                        className="mr-2"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="success"
                                        onClick={this.handleCreateNewBrandAndProduct}
                                        disabled={productsLoading || !newBrandNameManual.trim() || !newProduct?.name?.trim()}
                                    >
                                        {productsLoading ? (
                                            <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Creating...</>
                                        ) : (
                                            <><i className="fa-sharp fa-regular fa-plus mr-1"></i> Create Product</>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Brand Selection Dropdown - FIRST */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="small">Brand</Form.Label>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        value={selectedBrand}
                                        onChange={this.handleBrandSelect}
                                    >
                                        <option value="">All Brands</option>
                                        {uniqueBrands.map(brand => (
                                            <option key={brand} value={brand}>{brand}</option>
                                        ))}
                                        <option value="add_new_brand">Add New Brand & Product...</option>
                                    </Form.Control>
                                </Form.Group>

                                {/* Product Selection Dropdown - SECOND */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="small">Product <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        value={manualProduct.product_id}
                                        onChange={this.handleProductSelect}
                                    >
                                        <option value="">Select a product...</option>
                                        {filteredProducts.map(product => {
                                            const productId = product.product_id || product.id;
                                            return (
                                                <option key={productId} value={productId}>
                                                    {selectedBrand
                                                        ? formatProductLabel({ ...product, brand: null })
                                                        : formatProductLabel(product)}
                                                </option>
                                            );
                                        })}
                                        <option value="add_new">Add New Product...</option>
                                    </Form.Control>
                                </Form.Group>

                                {/* Add New Product Form (for existing brand) */}
                                {showAddNewProduct && (
                                    <div className="add-new-product-form">
                                        {/* Show selected brand as read-only info */}
                                        {selectedBrand ? (
                                            <div className="mb-2">
                                                <small className="text-muted">Brand:</small>
                                                <div className="selected-brand-display">{selectedBrand}</div>
                                            </div>
                                        ) : (
                                            <Alert variant="warning" className="mb-2 py-2">
                                                <small><i className="fa-sharp fa-regular fa-exclamation-triangle mr-1"></i> Please select a brand first</small>
                                            </Alert>
                                        )}
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Product Name <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={newProduct?.name || ''}
                                                onChange={(e) => this.handleNewProductChange('name', e.target.value)}
                                                placeholder="e.g., Ultimate Plus PPF"
                                                disabled={!selectedBrand}
                                            />
                                        </Form.Group>
                                        <Button
                                            size="sm"
                                            variant="success"
                                            onClick={this.handleCreateNewProduct}
                                            disabled={productsLoading || !newProduct?.name?.trim() || !selectedBrand}
                                            className="mb-2"
                                        >
                                            {productsLoading ? (
                                                <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Creating...</>
                                            ) : (
                                                <><i className="fa-sharp fa-regular fa-plus mr-1"></i> Create Product</>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Inventory Stock Section — only for a shop that tracks stock.
                            A "Record Used" shop has no stock rows by design, so this
                            block would show them a permanent amber "no inventory"
                            warning and then require them to invent a receipt before
                            they could record a usage. */}
                        {shopTracksInventory(this.props) && manualProduct.product_id && !showAddNewProduct && !showAddNewBrandManual && (
                            <>
                                {manualInventoryLoading ? (
                                    <div className="text-muted small py-2 text-center">
                                        <i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Checking inventory...
                                    </div>
                                ) : manualInventoryItems.length > 0 && !showManualReceive ? (
                                    <div className="inventory-picker mb-2">
                                        <Form.Label className="small">Available Stock</Form.Label>
                                        <div className="inventory-item-list">
                                            {manualInventoryItems.map(item => (
                                                <div
                                                    key={item.id}
                                                    className={`inventory-item-option ${selectedInventoryItemId === item.id ? 'selected' : ''}`}
                                                    onClick={() => this.handleInventoryItemSelect(item.id)}
                                                >
                                                    <Form.Check
                                                        type="radio"
                                                        name="inventoryItem"
                                                        checked={selectedInventoryItemId === item.id}
                                                        onChange={() => this.handleInventoryItemSelect(item.id)}
                                                        label={
                                                            <span>
                                                                {item.barcode ? <code>{item.barcode}</code> : <span className="text-muted">No barcode</span>}
                                                                {item.lot_number && <span className="text-muted ml-2">Lot: {item.lot_number}</span>}
                                                                <span className="ml-2">— {parseFloat(item.quantity_remaining).toFixed(2)} remaining</span>
                                                            </span>
                                                        }
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="link"
                                            className="p-0 mt-1"
                                            onClick={this.handleShowManualReceive}
                                        >
                                            <i className="fa-sharp fa-regular fa-plus mr-1"></i>Receive new stock instead
                                        </Button>
                                    </div>
                                ) : showManualReceive ? (
                                    <div className="inline-receive-form mb-2">
                                        {manualInventoryItems.length === 0 && (
                                            <Alert variant="warning" className="mb-2 py-2">
                                                <small>
                                                    <i className="fa-sharp fa-regular fa-exclamation-triangle mr-1"></i>
                                                    No inventory in stock for this product
                                                </small>
                                            </Alert>
                                        )}
                                        <Form.Label className="small">Receive Inventory</Form.Label>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Quantity to Receive <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                step="0.01"
                                                min="0"
                                                value={manualReceiveQty}
                                                onChange={(e) => this.setState({ manualReceiveQty: e.target.value })}
                                                placeholder="Amount to receive"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Barcode (optional)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={manualReceiveBarcode}
                                                onChange={(e) => this.setState({ manualReceiveBarcode: e.target.value })}
                                                placeholder="Scan or type barcode"
                                            />
                                        </Form.Group>
                                        <div className="d-flex align-items-center">
                                            <Button
                                                size="sm"
                                                variant="success"
                                                onClick={this.handleManualReceiveSubmit}
                                                disabled={manualReceiveLoading || !manualReceiveQty || parseFloat(manualReceiveQty) <= 0}
                                            >
                                                {manualReceiveLoading ? (
                                                    <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Receiving...</>
                                                ) : (
                                                    <><i className="fa-sharp fa-regular fa-box-check mr-1"></i> Receive & Use</>
                                                )}
                                            </Button>
                                            {manualInventoryItems.length > 0 && (
                                                <Button
                                                    size="sm"
                                                    variant="link"
                                                    className="ml-2"
                                                    onClick={() => this.setState({ showManualReceive: false })}
                                                >
                                                    Back to stock list
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </>
                        )}

                        {/* Quantity Input */}
                        <Form.Group className="mb-2">
                            <Form.Label className="small">
                                Quantity Used {' '}<span className="text-danger">*</span>
                            </Form.Label>
                            <div className="quantity-input-with-unit">
                                <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    min="0"
                                    value={manualProduct.quantity_used}
                                    onChange={(e) => this.handleManualQuantityChange(e.target.value)}
                                    placeholder="Amount used"
                                    className={showManualStockWarning ? 'is-warning' : ''}
                                />
                            </div>
                        </Form.Group>

                        {showManualStockWarning && (
                            <div className="quantity-warning mb-2">
                                <i className="fa-sharp fa-regular fa-exclamation-triangle"></i>
                                {' '}Quantity exceeds available stock ({selectedItemQtyRemaining?.toFixed(2)} remaining)
                            </div>
                        )}

                        <div className="manual-entry-actions">
                            <Button size="sm" variant="secondary" onClick={this.handleHideManualEntry}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={this.handleManualProductSubmit}
                                // The inventory clause applies only where inventory
                                // is tracked. For a "Record Used" shop there is no
                                // item to select and requiring one blocks the only
                                // thing they came here to do.
                                disabled={
                                  !manualProduct.product_name
                                  || showAddNewProduct
                                  || (shopTracksInventory(this.props)
                                      && (manualInventoryLoading || (showManualReceive && !selectedInventoryItemId)))
                                }
                            >
                                Add Material
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    renderScannerModal() {
        const { showScanner, scannerError } = this.state;

        return (
            <Modal
                show={showScanner}
                onHide={this.handleCloseScanner}
                centered
                size="lg"
                className="material-scanner-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa-sharp fa-regular fa-barcode mr-2"></i>
                        Scan Material Barcode
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {scannerError ? (
                        <div className="p-4 text-center text-danger">
                            <i className="fa-sharp fa-regular fa-exclamation-triangle fa-2x mb-2"></i>
                            <p>{scannerError}</p>
                        </div>
                    ) : (
                        <div className="scanner-container" style={{ minHeight: 350 }}>
                            <BarcodeScanner
                                formats={['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code']}
                                onDetected={this.handleScanSuccess}
                                onError={(msg) => this.setState({ scannerError: msg })}
                                active={showScanner}
                                scanRegion={{ widthPercent: 0.75, heightPercent: 0.25 }}
                                throttleMs={100}
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleCloseScanner}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderCategoryMismatchDialog() {
        const { showCategoryMismatch, mismatchBarcode, mismatchProductInfo } = this.state;

        if (!showCategoryMismatch || !mismatchProductInfo) return null;

        return (
            <div className="category-mismatch-dialog">
                <div className="mismatch-header">
                    <strong>
                        <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2 text-warning"></i>
                        Category Mismatch
                    </strong>
                    <button className="close-btn" onClick={this.handleCloseCategoryMismatch}>
                        <i className="fa-sharp fa-regular fa-times"></i>
                    </button>
                </div>

                <div className="barcode-display">
                    <small className="text-muted">Scanned barcode:</small>
                    <code>{mismatchBarcode}</code>
                </div>

                <div className="mismatch-info">
                    <p>
                        This barcode is registered for <strong>{mismatchProductInfo.category_name}</strong>
                        {mismatchProductInfo.brand && mismatchProductInfo.product_name && (
                            <span> ({formatProductLabel(mismatchProductInfo)})</span>
                        )}
                    </p>
                    <p>
                        But this service requires <strong>{mismatchProductInfo.expected_category_name}</strong> materials.
                    </p>
                </div>

                <div className="mismatch-options">
                    <p className="options-label">What would you like to do?</p>
                    <div className="mismatch-actions">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={this.handleCloseCategoryMismatch}
                        >
                            <i className="fa-sharp fa-regular fa-times mr-1"></i>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={this.handleAddForCurrentCategory}
                        >
                            <i className="fa-sharp fa-regular fa-plus mr-1"></i>
                            Add for {mismatchProductInfo.expected_category_name}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    renderProductSelectionDialog() {
        const { showProductSelection, multipleProducts, pendingBarcode } = this.state;

        if (!showProductSelection || !multipleProducts.length) return null;

        return (
            <div className="product-selection-dialog">
                <div className="selection-header">
                    <strong>
                        <i className="fa-sharp fa-regular fa-list mr-2 text-info"></i>
                        Select Product
                    </strong>
                    <button className="close-btn" onClick={this.handleCancelProductSelection}>
                        <i className="fa-sharp fa-regular fa-times"></i>
                    </button>
                </div>

                <div className="barcode-display">
                    <small className="text-muted">Scanned barcode:</small>
                    <code>{pendingBarcode}</code>
                </div>

                <div className="selection-info">
                    <p>This barcode exists for multiple products. Please select which product you're using:</p>
                </div>

                <div className="product-options">
                    {multipleProducts.map((item, index) => (
                        <div
                            key={item.id || index}
                            className="product-option"
                            onClick={() => this.handleSelectProduct(item)}
                        >
                            <div className="product-details">
                                <strong>{formatProductLabel(item)}</strong>
                                <small className="text-muted d-block">
                                    {item.sku && `${item.sku} • `}
                                    {item.category_name && `${item.category_name} • `}
                                    {item.quantity_remaining} {item.unit_code || 'units'} remaining
                                </small>
                            </div>
                            <i className="fa-sharp fa-regular fa-chevron-right text-muted"></i>
                        </div>
                    ))}
                </div>

                <style>{`
                    .product-selection-dialog {
                        background: #f0f7ff;
                        border: 1px solid #b8daff;
                        border-radius: 6px;
                        padding: 16px;
                        margin-bottom: 16px;
                    }
                    .selection-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 12px;
                    }
                    .selection-header .close-btn {
                        background: none;
                        border: none;
                        color: #6c757d;
                        cursor: pointer;
                        padding: 4px 8px;
                    }
                    .selection-header .close-btn:hover {
                        color: #343a40;
                    }
                    .product-selection-dialog .barcode-display {
                        background: white;
                        padding: 8px 12px;
                        border-radius: 4px;
                        margin-bottom: 12px;
                    }
                    .product-selection-dialog .barcode-display code {
                        font-size: 14px;
                        margin-left: 8px;
                    }
                    .selection-info {
                        margin-bottom: 12px;
                    }
                    .selection-info p {
                        margin: 0;
                        color: #495057;
                    }
                    .product-options {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    .product-option {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        background: white;
                        border: 1px solid #dee2e6;
                        border-radius: 6px;
                        padding: 12px 16px;
                        cursor: pointer;
                        transition: all 0.15s ease;
                    }
                    .product-option:hover {
                        background: #e9ecef;
                        border-color: #adb5bd;
                    }
                    .product-details {
                        flex: 1;
                    }
                `}</style>
            </div>
        );
    }

    renderBarcodeNotFoundForm() {
        const { showBarcodeNotFound, notFoundBarcode, notFoundForm, isNewProduct, products, productsLoading, showAddNewBrand, newBrandName, notFoundSelectedBrand, isAddingNewProductName } = this.state;

        if (!showBarcodeNotFound) return null;

        const existingBrands = this.getUniqueBrands();
        const productsForBrand = this.getProductsForSelectedBrand();
        const unitName = this.getUnitName();

        return (
            <div className="barcode-not-found-form">
                <div className="not-found-header">
                    <strong>
                        <i className="fa-sharp fa-regular fa-exclamation-circle mr-2 text-warning"></i>
                        Barcode Not Found
                    </strong>
                    <button className="close-btn" onClick={this.handleCloseBarcodeNotFound}>
                        <i className="fa-sharp fa-regular fa-times"></i>
                    </button>
                </div>

                <div className="barcode-display">
                    <small className="text-muted">Scanned barcode:</small>
                    <code>{notFoundBarcode}</code>
                </div>

                <p className="help-text">
                    This barcode is not in inventory. Please identify the product and add it:
                </p>

                {productsLoading && products.length === 0 ? (
                    <div className="text-center p-3">
                        <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                        Loading...
                    </div>
                ) : (
                    <>
                        {/* Product Selection - existing or new */}
                        <Form.Group className="mb-2">
                            <Form.Label className="small">Product</Form.Label>
                            <Form.Control
                                as="select"
                                size="sm"
                                value={isNewProduct ? 'add_new' : notFoundForm.product_id}
                                onChange={this.handleNotFoundProductChange}
                            >
                                <option value="add_new">+ Create New Product</option>
                                {[...products].sort((a, b) => {
                                    const nameA = (a.product_name || a.name || '').toLowerCase();
                                    const nameB = (b.product_name || b.name || '').toLowerCase();
                                    return nameA.localeCompare(nameB);
                                }).map(product => {
                                    const productId = product.product_id || product.id;
                                    const unitName = this.getUnitNameById(product.unit_id);
                                    const unitDisplay = unitName ? ` (${unitName})` : '';
                                    return (
                                        <option key={productId} value={productId}>
                                            {formatProductLabel(product)}{unitDisplay}
                                        </option>
                                    );
                                })}
                            </Form.Control>
                        </Form.Group>

                        {/* New Product Fields */}
                        {isNewProduct && (
                            <div className="new-product-fields">
                                {/* Brand Selection */}
                                <Form.Group className="mb-2">
                                    <Form.Label className="small">Brand</Form.Label>
                                    {!showAddNewBrand ? (
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={notFoundSelectedBrand || notFoundForm.brand}
                                            onChange={this.handleNotFoundBrandChange}
                                        >
                                            <option value="">Select a brand...</option>
                                            {existingBrands.map(brand => (
                                                <option key={brand} value={brand}>{brand}</option>
                                            ))}
                                            <option value="add_new">Add New Brand</option>
                                        </Form.Control>
                                    ) : (
                                        <div className="d-flex align-items-center">
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={newBrandName}
                                                onChange={this.handleNotFoundNewBrandChange}
                                                placeholder="Enter new brand name"
                                                autoFocus
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="ml-2"
                                                onClick={this.handleCancelNotFoundNewBrand}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </Form.Group>

                                {/* Show products for selected brand - allow selecting existing product */}
                                {notFoundSelectedBrand && productsForBrand.length > 0 && !isAddingNewProductName && (
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Existing Products for {notFoundSelectedBrand}</Form.Label>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={notFoundForm.product_id || ''}
                                            onChange={this.handleNotFoundBrandProductSelect}
                                        >
                                            <option value="">Select existing product or add new...</option>
                                            {productsForBrand.map(product => {
                                                const productId = product.product_id || product.id;
                                                const productName = product.product_name || product.name;
                                                const unitName = this.getUnitNameById(product.unit_id);
                                                const unitDisplay = unitName ? ` (${unitName})` : '';
                                                return (
                                                    <option key={productId} value={productId}>
                                                        {productName}{unitDisplay}
                                                    </option>
                                                );
                                            })}
                                            <option value="add_new">Add New Product for {notFoundSelectedBrand}</option>
                                        </Form.Control>
                                    </Form.Group>
                                )}

                                {/* Product Name - show when adding new brand, no products for brand, or explicitly adding new product */}
                                {(showAddNewBrand || !notFoundSelectedBrand || productsForBrand.length === 0 || isAddingNewProductName) && (
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Product Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            value={notFoundForm.product_name}
                                            onChange={(e) => this.handleNotFoundFormChange('product_name', e.target.value)}
                                            placeholder="e.g., Ultimate Plus PPF"
                                        />
                                    </Form.Group>
                                )}
                            </div>
                        )}

                        {/* Existing product info display */}
                        {!isNewProduct && notFoundForm.product_id && (
                            <div className="selected-product-info mb-2">
                                <small className="text-muted">Selected:</small>
                                <div className="selected-product-name">
                                    {notFoundForm.brand && <span className="brand">{notFoundForm.brand} </span>}
                                    {notFoundForm.product_name}
                                </div>
                            </div>
                        )}

                        {/* Quantity on Roll */}
                        <Form.Group className="mb-2">
                            <Form.Label className="small">
                                Quantity on Roll{unitName ? ` (${unitName})` : ''} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                size="sm"
                                step="0.01"
                                min="0"
                                value={notFoundForm.quantity_on_roll}
                                onChange={(e) => this.handleNotFoundFormChange('quantity_on_roll', e.target.value)}
                                placeholder={unitName ? `Total ${unitName.toLowerCase()} on this roll` : 'Total quantity on this roll'}
                            />
                        </Form.Group>

                        {/* Quantity Used */}
                        <Form.Group className="mb-2">
                            <Form.Label className="small">
                                Quantity Used{unitName ? ` (${unitName})` : ''} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                size="sm"
                                step="0.01"
                                min="0"
                                value={notFoundForm.quantity_used}
                                onChange={(e) => this.handleNotFoundFormChange('quantity_used', e.target.value)}
                                placeholder={unitName ? `${unitName} used for this service` : 'Amount used for this service'}
                            />
                        </Form.Group>

                        <div className="not-found-actions">
                            <Button size="sm" variant="secondary" onClick={this.handleCloseBarcodeNotFound}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                variant="primary"
                                onClick={this.handleNotFoundSubmit}
                                disabled={productsLoading}
                            >
                                {productsLoading ? (
                                    <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i> Saving...</>
                                ) : (
                                    <><i className="fa-sharp fa-regular fa-plus mr-1"></i> Add to Inventory</>
                                )}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    render() {
        const { scannedMaterials, manualBarcode, lookupError, lookupLoading } = this.state;
        const isMobile = this.isMobile();

        return (
            <div className="material-scanning-section">
                <div className="section-header">
                    <h6>
                        <i className="fa-sharp fa-regular fa-barcode mr-2"></i>
                        Materials Used
                    </h6>
                </div>

                {/* Scanned Materials List - Show first */}
                {scannedMaterials.length > 0 && (
                    <div className="scanned-materials-list">
                        {scannedMaterials.map((material, index) =>
                            this.renderScannedMaterial(material, index)
                        )}
                    </div>
                )}

                {/* Error Message */}
                {lookupError && (
                    <Alert variant="danger" className="mt-2 mb-2" dismissible onClose={() => this.setState({ lookupError: null })}>
                        <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
                        {lookupError}
                    </Alert>
                )}

                {/* Manual Entry Form */}
                {this.renderManualEntryForm()}

                {/* Category Mismatch Dialog */}
                {this.renderCategoryMismatchDialog()}

                {/* Product Selection Dialog (multiple products with same barcode) */}
                {this.renderProductSelectionDialog()}

                {/* Barcode Not Found Form */}
                {this.renderBarcodeNotFoundForm()}

                {/* Add Material Controls - Always visible at bottom */}
                {!this.state.showManualEntry && !this.state.showBarcodeNotFound && !this.state.showCategoryMismatch && !this.state.showProductSelection && (
                    <div className={`add-material-section ${scannedMaterials.length > 0 ? 'mt-3' : ''}`}>
                        {scannedMaterials.length > 0 && (
                            <div className="add-material-label">
                                <small className="text-muted">Add another material:</small>
                            </div>
                        )}
                        <div className="scan-controls">
                            {isMobile && (
                                <Button
                                    variant="outline-primary"
                                    onClick={this.handleOpenScanner}
                                    className="scan-btn"
                                >
                                    <i className="fa-sharp fa-regular fa-camera mr-2"></i>
                                    Scan Barcode
                                </Button>
                            )}

                            <Form onSubmit={this.handleManualBarcodeSubmit} className="manual-entry">
                                <Form.Control
                                    type="text"
                                    value={manualBarcode}
                                    onChange={this.handleManualBarcodeChange}
                                    placeholder="Enter barcode..."
                                    disabled={lookupLoading}
                                />
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={lookupLoading || !manualBarcode.trim()}
                                >
                                    {lookupLoading ? (
                                        <i className="fa-sharp fa-regular fa-spinner fa-spin"></i>
                                    ) : (
                                        <i className="fa-sharp fa-regular fa-plus"></i>
                                    )}
                                </Button>
                            </Form>
                        </div>
                        <div className="manual-entry-link">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                onClick={this.handleShowManualEntry}
                            >
                                <i className="fa-sharp fa-regular fa-edit mr-1"></i>
                                No barcode available? Add manually
                            </button>
                        </div>
                    </div>
                )}

                {/* Scanner Modal */}
                {this.renderScannerModal()}

                <style>{`
                    .material-scanning-section {
                        background: #f8fafc;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 16px;
                        margin-top: 16px;
                    }

                    .section-header {
                        margin-bottom: 12px;
                    }

                    .section-header h6 {
                        margin: 0;
                        color: #374151;
                        font-weight: 600;
                    }

                    .scan-controls {
                        display: flex;
                        gap: 12px;
                        align-items: center;
                        flex-wrap: wrap;
                    }

                    .scan-btn {
                        white-space: nowrap;
                    }

                    .manual-entry {
                        display: flex;
                        flex: 1;
                        min-width: 200px;
                        gap: 8px;
                    }

                    .manual-entry .form-control {
                        flex: 1;
                    }

                    .scanned-materials-list {
                        margin-top: 16px;
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }

                    .scanned-material-card {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 12px;
                    }

                    .material-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 8px;
                    }

                    .material-name {
                        font-weight: 600;
                        color: #374151;
                    }

                    .material-name .brand {
                        color: #6b7280;
                        font-weight: normal;
                        margin-right: 4px;
                    }

                    .remove-btn {
                        background: none;
                        border: none;
                        color: #9ca3af;
                        padding: 4px 8px;
                        cursor: pointer;
                        border-radius: 4px;
                    }

                    .remove-btn:hover {
                        background: #fee2e2;
                        color: #dc2626;
                    }

                    .material-details {
                        font-size: 13px;
                        color: #6b7280;
                        margin-bottom: 12px;
                    }

                    .detail-row {
                        display: flex;
                        gap: 8px;
                        margin-bottom: 4px;
                    }

                    .detail-row .label {
                        color: #9ca3af;
                    }

                    .detail-row code {
                        background: #f3f4f6;
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-size: 12px;
                    }

                    .quantity-input-row {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .quantity-input-row label {
                        margin: 0;
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        white-space: nowrap;
                    }

                    .quantity-input-group {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex: 1;
                        max-width: 200px;
                    }

                    .quantity-input-group .form-control {
                        text-align: right;
                    }

                    .quantity-input-group .form-control.is-warning {
                        border-color: #f59e0b;
                        background-color: #fef3c7;
                    }

                    .unit-label {
                        color: #6b7280;
                        font-size: 13px;
                        min-width: 40px;
                    }

                    .quantity-input-with-unit {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .quantity-input-with-unit .form-control {
                        flex: 1;
                    }

                    .unit-label-inline {
                        color: #6b7280;
                        font-size: 13px;
                        white-space: nowrap;
                    }

                    .unit-badge {
                        background: #e5e7eb;
                        color: #4b5563;
                        font-size: 11px;
                        padding: 2px 6px;
                        border-radius: 4px;
                        margin-left: 8px;
                        font-weight: 500;
                    }

                    .quantity-warning {
                        margin-top: 8px;
                        color: #d97706;
                        font-size: 12px;
                    }

                    .quantity-warning i {
                        margin-right: 4px;
                    }

                    .add-material-section {
                        border-top: 1px dashed #e5e7eb;
                        padding-top: 12px;
                    }

                    .add-material-label {
                        margin-bottom: 8px;
                    }

                    .manual-entry-link {
                        margin-top: 8px;
                        text-align: center;
                    }

                    .manual-entry-form {
                        background: #fefce8;
                        border: 1px solid #fde047;
                        border-radius: 8px;
                        padding: 12px;
                        margin-top: 12px;
                    }

                    .manual-entry-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 12px;
                    }

                    .manual-entry-header .close-btn {
                        background: none;
                        border: none;
                        color: #9ca3af;
                        cursor: pointer;
                        padding: 4px;
                    }

                    .manual-entry-header .close-btn:hover {
                        color: #374151;
                    }

                    .manual-entry-actions {
                        display: flex;
                        justify-content: flex-end;
                        gap: 8px;
                        margin-top: 12px;
                    }

                    .manual-entry-card {
                        border-color: #fde047;
                        background: #fffef0;
                    }

                    .manual-badge {
                        background: #fef08a;
                        color: #854d0e;
                        font-size: 10px;
                        padding: 2px 6px;
                        border-radius: 4px;
                        margin-left: 8px;
                        font-weight: 500;
                    }

                    .inventory-picker {
                        background: #f0f9ff;
                        border: 1px solid #bae6fd;
                        border-radius: 6px;
                        padding: 10px;
                    }

                    .inventory-item-list {
                        display: flex;
                        flex-direction: column;
                        gap: 6px;
                    }

                    .inventory-item-option {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        padding: 8px 10px;
                        cursor: pointer;
                        transition: border-color 0.15s;
                    }

                    .inventory-item-option:hover {
                        border-color: #93c5fd;
                    }

                    .inventory-item-option.selected {
                        border-color: #3b82f6;
                        background: #eff6ff;
                    }

                    .inventory-item-option .custom-control-label {
                        cursor: pointer;
                        font-size: 13px;
                    }

                    .inventory-item-option code {
                        background: #f3f4f6;
                        padding: 1px 5px;
                        border-radius: 3px;
                        font-size: 12px;
                    }

                    .inline-receive-form {
                        background: #fff7ed;
                        border: 1px solid #fed7aa;
                        border-radius: 6px;
                        padding: 10px;
                    }

                    .quantity-input-with-unit .form-control.is-warning {
                        border-color: #f59e0b;
                        background-color: #fef3c7;
                    }

                    .add-new-product-form {
                        background: #f0fdf4;
                        border: 1px solid #86efac;
                        border-radius: 6px;
                        padding: 10px;
                        margin-bottom: 10px;
                    }

                    .selected-brand-display {
                        font-weight: 600;
                        color: #374151;
                        background: #e5e7eb;
                        padding: 4px 8px;
                        border-radius: 4px;
                        display: inline-block;
                    }

                    .selected-product-display {
                        background: #f8fafc;
                        border-radius: 4px;
                        padding: 8px;
                    }

                    .selected-product-name {
                        font-weight: 500;
                        color: #374151;
                    }

                    .selected-product-name .brand {
                        color: #6b7280;
                        font-weight: normal;
                    }

                    .material-scanner-modal .modal-body {
                        min-height: 350px;
                    }

                    /* Category Mismatch Dialog Styles */
                    .category-mismatch-dialog {
                        background: #fef3c7;
                        border: 1px solid #f59e0b;
                        border-radius: 8px;
                        padding: 16px;
                        margin-top: 12px;
                    }

                    .mismatch-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 12px;
                    }

                    .mismatch-header .close-btn {
                        background: none;
                        border: none;
                        color: #9ca3af;
                        cursor: pointer;
                        padding: 4px;
                    }

                    .mismatch-header .close-btn:hover {
                        color: #374151;
                    }

                    .mismatch-info {
                        background: white;
                        border-radius: 6px;
                        padding: 12px;
                        margin-bottom: 12px;
                    }

                    .mismatch-info p {
                        margin-bottom: 8px;
                        color: #374151;
                    }

                    .mismatch-info p:last-child {
                        margin-bottom: 0;
                    }

                    .mismatch-options {
                        padding-top: 12px;
                        border-top: 1px solid #fde68a;
                    }

                    .options-label {
                        font-size: 13px;
                        color: #78350f;
                        margin-bottom: 8px;
                    }

                    .mismatch-actions {
                        display: flex;
                        justify-content: flex-end;
                        gap: 8px;
                    }

                    /* Barcode Not Found Form Styles */
                    .barcode-not-found-form {
                        background: #fef3c7;
                        border: 1px solid #f59e0b;
                        border-radius: 8px;
                        padding: 16px;
                        margin-top: 12px;
                    }

                    .not-found-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 12px;
                    }

                    .not-found-header .close-btn {
                        background: none;
                        border: none;
                        color: #9ca3af;
                        cursor: pointer;
                        padding: 4px;
                    }

                    .not-found-header .close-btn:hover {
                        color: #374151;
                    }

                    .barcode-display {
                        background: white;
                        border-radius: 4px;
                        padding: 8px 12px;
                        margin-bottom: 12px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .barcode-display code {
                        background: #f3f4f6;
                        padding: 4px 8px;
                        border-radius: 4px;
                        font-size: 14px;
                        font-weight: 600;
                    }

                    .help-text {
                        font-size: 13px;
                        color: #78350f;
                        margin-bottom: 12px;
                    }

                    .new-product-fields {
                        background: #fffbeb;
                        border: 1px dashed #fbbf24;
                        border-radius: 6px;
                        padding: 12px;
                        margin-bottom: 12px;
                    }

                    .selected-product-info {
                        background: white;
                        border-radius: 4px;
                        padding: 8px;
                    }

                    .not-found-actions {
                        display: flex;
                        justify-content: flex-end;
                        gap: 8px;
                        margin-top: 12px;
                        padding-top: 12px;
                        border-top: 1px solid #fde68a;
                    }

                    @media (max-width: 576px) {
                        .scan-controls {
                            flex-direction: column;
                        }

                        .scan-btn {
                            width: 100%;
                        }

                        .manual-entry {
                            width: 100%;
                        }

                        .quantity-input-row {
                            flex-direction: column;
                            align-items: flex-start;
                            gap: 8px;
                        }

                        .quantity-input-group {
                            width: 100%;
                            max-width: none;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

export default MaterialScanningSection;
